import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../App';
import AdminLayout from './AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { ShieldCheck, ShieldOff, Copy, Download, Eye, EyeOff, RefreshCw } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdminSecurity() {
  const { user, fetchUser } = useContext(AuthContext);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  // Setup flow
  const [setupData, setSetupData] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [verifying, setVerifying] = useState(false);

  // Disable flow
  const [showDisable, setShowDisable] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  const [showDisablePassword, setShowDisablePassword] = useState(false);
  const [disabling, setDisabling] = useState(false);

  // Staff 2FA management
  const [staffList, setStaffList] = useState([]);

  const fetchStatus = async () => {
    try {
      const res = await axios.get(`${API}/auth/2fa/status`);
      setStatus(res.data);
    } catch {
      setStatus({ enabled: false });
    } finally {
      setLoading(false);
    }
  };

  const fetchStaff = async () => {
    if (user?.role !== 'admin') return;
    try {
      const res = await axios.get(`${API}/admin/users`);
      const users = Array.isArray(res.data) ? res.data : res.data.users || [];
      setStaffList(users.filter(u => u.role === 'admin' || u.role === 'staff'));
    } catch { /* ignore */ }
  };

  useEffect(() => {
    fetchStatus();
    fetchStaff();
  }, []);

  const handleSetup = async () => {
    try {
      const res = await axios.post(`${API}/auth/2fa/setup`);
      setSetupData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start 2FA setup');
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setVerifying(true);
    try {
      await axios.post(`${API}/auth/2fa/verify`, { code: verifyCode });
      toast.success('Two-factor authentication enabled!');
      setSetupData(null);
      setVerifyCode('');
      fetchStatus();
      fetchUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid code');
    } finally {
      setVerifying(false);
    }
  };

  const handleDisable = async (e) => {
    e.preventDefault();
    setDisabling(true);
    try {
      await axios.post(`${API}/auth/2fa/disable`, { password: disablePassword });
      toast.success('Two-factor authentication disabled');
      setShowDisable(false);
      setDisablePassword('');
      fetchStatus();
      fetchUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to disable 2FA');
    } finally {
      setDisabling(false);
    }
  };

  const handleResetStaff2FA = async (userId, email) => {
    if (!window.confirm(`Reset 2FA for ${email}? They will need to set it up again.`)) return;
    try {
      await axios.post(`${API}/admin/users/${userId}/reset-2fa`);
      toast.success(`2FA reset for ${email}`);
      fetchStaff();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset 2FA');
    }
  };

  const copyBackupCodes = () => {
    if (setupData?.backup_codes) {
      navigator.clipboard.writeText(setupData.backup_codes.join('\n'));
      toast.success('Backup codes copied to clipboard');
    }
  };

  const downloadBackupCodes = () => {
    if (setupData?.backup_codes) {
      const text = `Tech Distribution Admin - 2FA Backup Codes\nGenerated: ${new Date().toLocaleDateString()}\n\nStore these codes in a safe place. Each code can only be used once.\n\n${setupData.backup_codes.join('\n')}`;
      const blob = new Blob([text], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'tech-distribution-2fa-backup-codes.txt';
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-6" data-testid="security-page">
          <h1 className="text-2xl font-bold mb-6">Security Settings</h1>
          <div className="text-muted-foreground">Loading...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 max-w-3xl" data-testid="security-page">
      <h1 className="text-2xl font-bold mb-6">Security Settings</h1>

      {/* 2FA Status Card */}
      <div className="bg-card border border-border rounded-lg p-6 mb-6" data-testid="2fa-status-card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${status?.enabled ? 'bg-green-500/10' : 'bg-yellow-500/10'}`}>
              {status?.enabled
                ? <ShieldCheck className="h-5 w-5 text-green-500" />
                : <ShieldOff className="h-5 w-5 text-yellow-500" />
              }
            </div>
            <div>
              <h2 className="font-semibold text-lg">Two-Factor Authentication</h2>
              <p className="text-sm text-muted-foreground">
                {status?.enabled
                  ? `Enabled${status.enabled_at ? ` on ${new Date(status.enabled_at).toLocaleDateString()}` : ''}`
                  : 'Not enabled — your account is less secure'
                }
              </p>
            </div>
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${status?.enabled ? 'bg-green-500/10 text-green-500' : 'bg-yellow-500/10 text-yellow-500'}`} data-testid="2fa-status-badge">
            {status?.enabled ? 'Active' : 'Inactive'}
          </span>
        </div>

        {status?.enabled && (
          <p className="text-sm text-muted-foreground mb-4">
            Backup codes remaining: <strong>{status.backup_codes_remaining}</strong>
          </p>
        )}

        {!status?.enabled && !setupData && (
          <Button
            onClick={handleSetup}
            className="bg-[#78BE20] text-black hover:bg-[#65a31b]"
            data-testid="enable-2fa-btn"
          >
            <ShieldCheck className="h-4 w-4 mr-2" /> Enable 2FA
          </Button>
        )}

        {status?.enabled && !showDisable && (
          <Button
            variant="outline"
            onClick={() => setShowDisable(true)}
            className="border-red-500/30 text-red-500 hover:bg-red-500/10"
            data-testid="disable-2fa-btn"
          >
            <ShieldOff className="h-4 w-4 mr-2" /> Disable 2FA
          </Button>
        )}

        {/* Disable 2FA Form */}
        {showDisable && (
          <form onSubmit={handleDisable} className="mt-4 p-4 border border-red-500/20 rounded-lg bg-red-500/5">
            <p className="text-sm text-red-400 mb-3">Enter your password to confirm disabling 2FA:</p>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Input
                  type={showDisablePassword ? 'text' : 'password'}
                  value={disablePassword}
                  onChange={(e) => setDisablePassword(e.target.value)}
                  placeholder="Your password"
                  className="pr-10"
                  required
                  data-testid="disable-2fa-password"
                />
                <button
                  type="button"
                  onClick={() => setShowDisablePassword(!showDisablePassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                >
                  {showDisablePassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <Button type="submit" disabled={disabling} variant="destructive" data-testid="confirm-disable-2fa">
                {disabling ? 'Disabling...' : 'Confirm'}
              </Button>
              <Button type="button" variant="outline" onClick={() => { setShowDisable(false); setDisablePassword(''); }}>
                Cancel
              </Button>
            </div>
          </form>
        )}
      </div>

      {/* Setup Flow */}
      {setupData && (
        <div className="bg-card border border-border rounded-lg p-6 mb-6" data-testid="2fa-setup-flow">
          <h2 className="font-semibold text-lg mb-4">Set Up Authenticator App</h2>

          <div className="space-y-6">
            {/* Step 1: Scan QR Code */}
            <div>
              <p className="text-sm text-muted-foreground mb-3">
                <strong>Step 1:</strong> Scan this QR code with Google Authenticator, Authy, or any TOTP app:
              </p>
              <div className="flex justify-center bg-white p-4 rounded-lg w-fit mx-auto">
                <img src={setupData.qr_code} alt="2FA QR Code" className="w-48 h-48" data-testid="2fa-qr-code" />
              </div>
              <p className="text-xs text-muted-foreground text-center mt-2">
                Or enter manually: <code className="bg-muted px-2 py-0.5 rounded text-xs">{setupData.secret}</code>
              </p>
            </div>

            {/* Step 2: Save Backup Codes */}
            <div>
              <p className="text-sm text-muted-foreground mb-3">
                <strong>Step 2:</strong> Save these backup codes. Each can be used once if you lose your device:
              </p>
              <div className="bg-muted rounded-lg p-4 font-mono text-sm grid grid-cols-2 gap-2" data-testid="backup-codes">
                {setupData.backup_codes.map((code, i) => (
                  <div key={i} className="text-center py-1">{code}</div>
                ))}
              </div>
              <div className="flex gap-2 mt-3">
                <Button variant="outline" size="sm" onClick={copyBackupCodes} data-testid="copy-backup-codes">
                  <Copy className="h-3 w-3 mr-1" /> Copy
                </Button>
                <Button variant="outline" size="sm" onClick={downloadBackupCodes} data-testid="download-backup-codes">
                  <Download className="h-3 w-3 mr-1" /> Download
                </Button>
              </div>
            </div>

            {/* Step 3: Verify */}
            <div>
              <p className="text-sm text-muted-foreground mb-3">
                <strong>Step 3:</strong> Enter the 6-digit code from your authenticator app to confirm:
              </p>
              <form onSubmit={handleVerify} className="flex gap-2">
                <Input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="000000"
                  value={verifyCode}
                  onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ''))}
                  className="w-40 text-center text-xl tracking-[0.3em] font-mono"
                  required
                  autoFocus
                  data-testid="2fa-verify-code"
                />
                <Button
                  type="submit"
                  disabled={verifying || verifyCode.length !== 6}
                  className="bg-[#78BE20] text-black hover:bg-[#65a31b]"
                  data-testid="2fa-verify-submit"
                >
                  {verifying ? 'Verifying...' : 'Verify & Enable'}
                </Button>
              </form>
            </div>
          </div>

          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSetupData(null)}
            className="mt-4 text-muted-foreground"
          >
            Cancel setup
          </Button>
        </div>
      )}

      {/* Staff 2FA Management (admin only) */}
      {user?.role === 'admin' && staffList.length > 0 && (
        <div className="bg-card border border-border rounded-lg p-6" data-testid="staff-2fa-management">
          <h2 className="font-semibold text-lg mb-4">Staff 2FA Status</h2>
          <p className="text-sm text-muted-foreground mb-4">
            View and manage 2FA for all admin and staff accounts.
          </p>
          <div className="space-y-2">
            {staffList.map(s => (
              <div key={s.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg" data-testid={`staff-2fa-row-${s.id}`}>
                <div className="flex items-center gap-3">
                  <div className={`w-2 h-2 rounded-full ${s.totp_enabled ? 'bg-green-500' : 'bg-yellow-500'}`} />
                  <div>
                    <p className="text-sm font-medium">{s.first_name} {s.last_name}</p>
                    <p className="text-xs text-muted-foreground">{s.email} — {s.role}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-0.5 rounded text-xs ${s.totp_enabled ? 'bg-green-500/10 text-green-500' : 'bg-yellow-500/10 text-yellow-500'}`}>
                    {s.totp_enabled ? '2FA Active' : 'No 2FA'}
                  </span>
                  {s.totp_enabled && s.id !== user.id && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleResetStaff2FA(s.id, s.email)}
                      className="text-red-500 hover:text-red-400 h-7 px-2"
                      data-testid={`reset-2fa-${s.id}`}
                    >
                      <RefreshCw className="h-3 w-3 mr-1" /> Reset
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
    </AdminLayout>
  );
}
